import {
  Box,
  Flex,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { Fragment, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import { useAppStore } from '../../../store/appStore/appStore'
import {
  ProbePassDetailsRow,
  ProbeStatusBadge,
} from '../shared/TableProbeUi.parts'
import { AppStoreTableProbeQueryContext } from '../shared/appStoreTableProbeQueryContext.parts'
import { PROBE_CONCURRENCY } from '../shared/probeConcurrency.parts'
import { TableProbeOrchestrator } from '../shared/tableProbeOrchestrator.parts'
import {
  DetailSubTableProbeRow,
  DetailSubTablesTest,
  ProbeStatus,
} from './DetailSubTablesTestPage.parts'

const DETAIL_SUB_TABLE_COLUMN_COUNT = 8

export const DetailSubTablesTestPage = () => {
  const viewConfigs = useAppStore(store => store.cms.viewConfigs)
  const [rows, setRows] = useState<DetailSubTableProbeRow[]>([])
  const [expandedQueryId, setExpandedQueryId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const targets = DetailSubTablesTest.collectDetailSubTables(viewConfigs)

    setRows(DetailSubTablesTest.toInitialProbeRows(targets))
    setExpandedQueryId(null)

    const runProbes = async () => {
      const context = AppStoreTableProbeQueryContext.create()

      await TableProbeOrchestrator.runDetailSubTableProbes(
        context,
        viewConfigs,
        {
          onBatchProgress: (queryIds, parentsChecked, parentTotalCount) => {
            if (cancelled) {
              return
            }

            setRows(current =>
              current.map(row =>
                queryIds.includes(row.queryId)
                  ? {
                      ...row,
                      recordsChecked: parentsChecked,
                      totalCount: parentTotalCount,
                    }
                  : row,
              ),
            )
          },
          onRowUpdate: (queryId, update) => {
            if (cancelled) {
              return
            }

            setRows(current =>
              current.map(row =>
                row.queryId === queryId
                  ? {
                      ...row,
                      ...update,
                    }
                  : row,
              ),
            )
          },
        },
      )
    }

    void runProbes()

    return () => {
      cancelled = true
      const parentListQueryIds = new Set(
        targets.map(target => target.parentListSource.parentListQueryId),
      )

      targets.forEach(target => {
        useAppStore
          .getState()
          .queryStore.removeQuery(
            DetailSubTablesTest.createProbeQueryId(target.queryId),
          )
      })

      parentListQueryIds.forEach(parentListQueryId => {
        useAppStore
          .getState()
          .queryStore.removeQuery(
            DetailSubTablesTest.createParentListProbeQueryId(parentListQueryId),
          )
      })
    }
  }, [viewConfigs])

  const isRunning = rows.some(row => row.status === 'running')
  const passCount = rows.filter(row => row.status === 'pass').length

  const toggleExpanded = (queryId: string, status: ProbeStatus) => {
    if (status === 'running') {
      return
    }

    setExpandedQueryId(current => (current === queryId ? null : queryId))
  }

  return (
    <AdminPageLayout pageTitle="Detail Sub-Tables Test" icon="CheckIcon">
      <Flex alignItems="center" gap="12px" mb="16px">
        <AdminBadge
          colorScheme={
            isRunning ? 'blue' : passCount === rows.length ? 'green' : 'orange'
          }
        >
          {isRunning
            ? 'Running probes...'
            : `${passCount}/${rows.length} sub-tables returned data`}
        </AdminBadge>
        {isRunning && <Spinner size="sm" />}
      </Flex>

      <Text fontSize="sm" color="presets.neutral.60" mb="12px">
        Detail sub-tables probe concurrently (up to {PROBE_CONCURRENCY} detail
        views at a time). Each sub-table stops at the first parent entity that
        returns rows.
      </Text>

      <Box
        border="1px solid"
        borderColor="presets.neutral.20"
        borderRadius="8px"
        overflow="hidden"
      >
        <Table size="sm" variant="simple">
          <Thead bg="presets.neutral.10">
            <Tr>
              <Th>Detail View</Th>
              <Th>Route</Th>
              <Th>Query ID</Th>
              <Th>Status</Th>
              <Th isNumeric>Parent Entities Checked</Th>
              <Th isNumeric>Parent Total Count</Th>
              <Th>Sample Value</Th>
              <Th>Parent Entity ID</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map(row => (
              <Fragment key={row.queryId}>
                <Tr
                  data-testid={`detail-sub-table-test-row-${row.queryId}`}
                  cursor={row.status === 'running' ? 'default' : 'pointer'}
                  onClick={() => toggleExpanded(row.queryId, row.status)}
                  _hover={
                    row.status === 'running'
                      ? undefined
                      : { bg: 'presets.neutral.5' }
                  }
                >
                  <Td fontWeight="600">{row.viewLabel}</Td>
                  <Td>
                    <Link
                      as={RouterLink}
                      to={row.passRecordPath ?? row.viewPath}
                      color="presets.turquoise.100"
                      fontSize="sm"
                      onClick={event => event.stopPropagation()}
                    >
                      {row.viewPath}
                    </Link>
                  </Td>
                  <Td>
                    <Box as="code" fontSize="xs">
                      {row.queryId}
                    </Box>
                  </Td>
                  <Td>
                    <ProbeStatusBadge
                      status={row.status}
                      data-testid={`detail-sub-table-test-status-${row.queryId}`}
                    />
                    {row.status === 'running' &&
                      row.recordsChecked !== undefined && (
                        <Text mt="4px" fontSize="xs" color="presets.neutral.60">
                          Checked {row.recordsChecked}
                          {row.totalCount !== undefined
                            ? ` / ${row.totalCount}`
                            : ''}
                        </Text>
                      )}
                  </Td>
                  <Td isNumeric>
                    {row.recordsChecked ?? (row.status === 'running' ? '—' : 0)}
                  </Td>
                  <Td isNumeric>
                    {row.totalCount ?? (row.status === 'running' ? '—' : 0)}
                  </Td>
                  <Td>
                    {row.sampleValue ?? (row.status === 'running' ? '—' : '—')}
                  </Td>
                  <Td>
                    <Box as="code" fontSize="xs">
                      {row.parentEntityId ?? '—'}
                    </Box>
                  </Td>
                </Tr>
                <ProbePassDetailsRow
                  columnCount={DETAIL_SUB_TABLE_COLUMN_COUNT}
                  isExpanded={expandedQueryId === row.queryId}
                  passRecordPath={row.passRecordPath}
                  recordsChecked={row.recordsChecked}
                  status={row.status}
                  errorMessage={row.errorMessage}
                />
              </Fragment>
            ))}
          </Tbody>
        </Table>
      </Box>
    </AdminPageLayout>
  )
}
